/**
 * Minimal WebGL2 helper for ping-pong fragment simulations.
 *
 * Reaction-diffusion, Physarum and the fluid solver are all the same shape of problem:
 * a state texture, a shader that reads it and writes the next state, and a display pass.
 * Rather than pulling in a rendering framework for that, this wraps exactly the pieces
 * those three need — a full-screen triangle, a shader cache, and float render targets.
 *
 * Everything is created against an `OffscreenCanvas` where available, so techniques can
 * run inside the batch worker like the Canvas2D ones do.
 */

export interface GlTarget {
  texture: WebGLTexture;
  framebuffer: WebGLFramebuffer;
  width: number;
  height: number;
}

export interface GlContext {
  canvas: OffscreenCanvas | HTMLCanvasElement;
  gl: WebGL2RenderingContext;
  /** Whether float textures can be rendered into; simulations need this. */
  floatTargets: boolean;
  quad: WebGLVertexArrayObject;
  createProgram(fragmentSource: string): WebGLProgram;
  createTarget(width: number, height: number, float?: boolean): GlTarget;
  bind(target: GlTarget | null, width?: number, height?: number): void;
  draw(program: WebGLProgram, uniforms: Record<string, number | number[] | GlTarget>): void;
  dispose(): void;
}

/**
 * A full-screen triangle rather than a quad: one fewer vertex, no diagonal seam, and
 * the fragment shader is invoked once per pixel either way.
 */
const VERTEX_SOURCE = `#version 300 es
out vec2 vUv;
void main() {
  vec2 pos = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  vUv = pos;
  gl_Position = vec4(pos * 2.0 - 1.0, 0.0, 1.0);
}`;

function compile(gl: WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('Shader konnte nicht erstellt werden.');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader) ?? 'unbekannter Fehler';
    gl.deleteShader(shader);
    throw new Error(`Shader-Fehler: ${log}`);
  }
  return shader;
}

export function createGlContext(width: number, height: number): GlContext | null {
  const canvas =
    typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(width, height) : document.createElement('canvas');
  if (!(canvas instanceof OffscreenCanvas)) {
    canvas.width = width;
    canvas.height = height;
  }

  const gl = canvas.getContext('webgl2', {
    alpha: true,
    antialias: false,
    premultipliedAlpha: false,
    preserveDrawingBuffer: true,
  }) as WebGL2RenderingContext | null;
  if (!gl) return null;

  // Rendering into float textures is an extension even in WebGL2. Without it the
  // simulations fall back to 8-bit state, which quantises the chemistry visibly.
  const floatTargets = Boolean(gl.getExtension('EXT_color_buffer_float'));
  gl.getExtension('OES_texture_float_linear');

  const vertexShader = compile(gl, gl.VERTEX_SHADER, VERTEX_SOURCE);
  const quad = gl.createVertexArray();
  if (!quad) return null;

  const programs = new Map<string, WebGLProgram>();
  const targets: GlTarget[] = [];

  const context: GlContext = {
    canvas,
    gl,
    floatTargets,
    quad,

    createProgram(fragmentSource) {
      const cached = programs.get(fragmentSource);
      if (cached) return cached;

      const program = gl.createProgram();
      if (!program) throw new Error('Programm konnte nicht erstellt werden.');
      const fragmentShader = compile(gl, gl.FRAGMENT_SHADER, fragmentSource);
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(`Link-Fehler: ${gl.getProgramInfoLog(program) ?? ''}`);
      }
      gl.deleteShader(fragmentShader);
      programs.set(fragmentSource, program);
      return program;
    },

    createTarget(w, h, float = true) {
      const texture = gl.createTexture();
      const framebuffer = gl.createFramebuffer();
      if (!texture || !framebuffer) throw new Error('Render-Target konnte nicht erstellt werden.');

      const useFloat = float && floatTargets;
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        useFloat ? gl.RGBA16F : gl.RGBA8,
        w,
        h,
        0,
        gl.RGBA,
        useFloat ? gl.HALF_FLOAT : gl.UNSIGNED_BYTE,
        null,
      );
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

      gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);

      const target: GlTarget = { texture, framebuffer, width: w, height: h };
      targets.push(target);
      return target;
    },

    bind(target, w, h) {
      if (target) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
        gl.viewport(0, 0, target.width, target.height);
      } else {
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        gl.viewport(0, 0, w ?? canvas.width, h ?? canvas.height);
      }
    },

    draw(program, uniforms) {
      gl.useProgram(program);
      gl.bindVertexArray(quad);

      let textureUnit = 0;
      for (const [name, value] of Object.entries(uniforms)) {
        const location = gl.getUniformLocation(program, name);
        if (location === null) continue;

        if (typeof value === 'number') {
          gl.uniform1f(location, value);
        } else if (Array.isArray(value)) {
          if (value.length === 2) gl.uniform2f(location, value[0], value[1]);
          else if (value.length === 3) gl.uniform3f(location, value[0], value[1], value[2]);
          else if (value.length === 4) gl.uniform4f(location, value[0], value[1], value[2], value[3]);
        } else {
          gl.activeTexture(gl.TEXTURE0 + textureUnit);
          gl.bindTexture(gl.TEXTURE_2D, value.texture);
          gl.uniform1i(location, textureUnit);
          textureUnit++;
        }
      }

      gl.drawArrays(gl.TRIANGLES, 0, 3);
    },

    dispose() {
      for (const target of targets) {
        gl.deleteTexture(target.texture);
        gl.deleteFramebuffer(target.framebuffer);
      }
      for (const program of programs.values()) gl.deleteProgram(program);
      gl.deleteVertexArray(quad);
      gl.deleteShader(vertexShader);
    },
  };

  return context;
}

/**
 * Uploads an 8-bit mask as a texture — used to feed the text field into a shader.
 */
export function uploadMask(context: GlContext, data: Uint8Array, width: number, height: number): GlTarget {
  const { gl } = context;
  const target = context.createTarget(width, height, false);
  gl.bindTexture(gl.TEXTURE_2D, target.texture);
  // The mask is single-channel; expand it so the shader can sample `.r` directly.
  const rgba = new Uint8Array(width * height * 4);
  for (let i = 0; i < data.length; i++) {
    rgba[i * 4] = data[i];
    rgba[i * 4 + 1] = data[i];
    rgba[i * 4 + 2] = data[i];
    rgba[i * 4 + 3] = 255;
  }
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, rgba);
  return target;
}

/** Uploads a float field (the SDF) as a texture. */
export function uploadField(context: GlContext, data: Float32Array, width: number, height: number, scale: number): GlTarget {
  const { gl } = context;
  const target = context.createTarget(width, height, false);
  gl.bindTexture(gl.TEXTURE_2D, target.texture);
  // Encoded into 8-bit with an explicit scale: enough precision for steering, and it
  // avoids requiring float *upload* support on top of float render targets.
  const bytes = new Uint8Array(width * height * 4);
  for (let i = 0; i < data.length; i++) {
    const v = Math.max(0, Math.min(255, Math.round(data[i] / scale + 128)));
    bytes[i * 4] = v;
    bytes[i * 4 + 1] = v;
    bytes[i * 4 + 2] = v;
    bytes[i * 4 + 3] = 255;
  }
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, bytes);
  return target;
}
